import type { Session, User } from '@supabase/supabase-js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useModal, useVfm } from 'vue-final-modal';

import ModalNames from '@/constants/ModalNames';
import { signIn, signInWithKeycloak, signOut } from '@/services/auth';
import { getByUserUidWithRole } from '@/services/employee';
import PinLoginModal from '@/views/core/PinLoginModal.vue';
import EmployeeModel from '@/models/employee/EmployeeModel';

export const useSessionStore = defineStore('session', () => {
  // Connected User (from auth)
  const connectedEmployee = ref<EmployeeModel | null>(null);

  // Display Employee (for UI display only - from PIN)
  const displayEmployee = ref<EmployeeModel | null>(null);
  const lastPinEmployee = ref<EmployeeModel | null>(null);

  function setConnectedEmployee(newEmployee: EmployeeModel | null) {
    connectedEmployee.value = newEmployee;

    // If connected user is admin or user and no display employee is set, show them
    if (newEmployee?.isAdminOrUser && !displayEmployee.value) {
      displayEmployee.value = newEmployee;
    }
  }

  function setDisplayEmployee(newEmployee: EmployeeModel) {
    displayEmployee.value = newEmployee;
  }

  function clearDisplayEmployee() {
    displayEmployee.value = null;

    // If connected user is admin or user, restore them for display
    if (connectedEmployee.value?.isAdminOrUser) {
      displayEmployee.value = connectedEmployee.value;
    }
  }

  function setLastPinEmployee(newEmployee: EmployeeModel) {
    lastPinEmployee.value = newEmployee;
    // For display purposes only - don't affect the connected session
    setDisplayEmployee(newEmployee);
  }

  function clearLastPinEmployee() {
    lastPinEmployee.value = null;

    // If connected user is admin or user, restore them for display
    if (connectedEmployee.value?.isAdminOrUser) {
      displayEmployee.value = connectedEmployee.value;
    } else {
      displayEmployee.value = null;
    }
  }

  // Check if connected user is admin
  const isConnectedUserAdmin = computed(() => {
    return connectedEmployee.value?.isAdmin ?? false;
  });

  // Check if connected user is admin or user
  const isConnectedUserAdminOrUser = computed(() => {
    return connectedEmployee.value?.isAdminOrUser ?? false;
  });

  // Check if PIN system should be bypassed (admin or user on their own account)
  const shouldBypassPin = computed(() => {
    return isConnectedUserAdminOrUser.value && (!displayEmployee.value || displayEmployee.value.uid === connectedEmployee.value?.uid);
  });

  async function openPinModal() {
    const { get } = useVfm();

    if (get(ModalNames.PinLogin) || !isAuthenticated.value) return;

    const { open } = useModal({ component: PinLoginModal });
    await open();
  }

  // User Session
  const user = ref<User | null>(null);
  const session = ref<Session | null>(null);
  const isAuthenticated = computed(() => !!user.value);

  async function setSession(newSession: Session | null) {
    session.value = newSession;
    user.value = newSession?.user ?? null;

    // Load employee data with role when session is set
    if (newSession?.user) {
      await loadEmployeeData(newSession.user.id);
    }
  }

  async function loadEmployeeData(userUid: string) {
    try {
      const { data, error } = await getByUserUidWithRole(userUid);

      if (!error && data) {
        const employeeModel = new EmployeeModel(data);
        setConnectedEmployee(employeeModel);
      } else {
        // User exists but no employee record - clear connected employee
        setConnectedEmployee(null);
      }
    } catch (err) {
      console.error('Error loading employee data:', err);
      setConnectedEmployee(null);
    } finally {
      // After loading employee data, check if PIN login is required
      if (!displayEmployee.value && !shouldBypassPin.value) {
        openPinModal();
      }
    }
  }

  function clearSession() {
    session.value = null;
    user.value = null;
    setConnectedEmployee(null);
    clearDisplayEmployee();
    clearLastPinEmployee();
  }

  async function login(email: string, password: string) {
    // Try to sign in first
    const { data, error } = await signIn(email, password);

    // If sign in fails, try to sign up the user (for development mode)
    if (error) {
      console.error('Sign in failed, attempting sign up:', error);
    }

    if (data?.session) {
      setSession(data.session);
    }

    return { data, error };
  }

  async function oauthLogin() {
    return await signInWithKeycloak();
  }

  async function logout() {
    const { data, error } = await signOut();

    if (data) {
      clearSession();
    }

    return { data, error };
  }

  // Expose the store's state and actions
  return {
    clearDisplayEmployee,
    clearLastPinEmployee,
    connectedEmployee,
    displayEmployee,
    isAuthenticated,
    isConnectedUserAdmin,
    lastPinEmployee,
    login,
    logout,
    oauthLogin,
    openPinModal,
    session,
    setLastPinEmployee,
    setSession,
    shouldBypassPin,
  };
});
